import { historyPageSchema, type HistoryFilter, type HistoryItem } from '@personal-os/shared';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { describeCommandError, undoChange } from '../core/commandClient';
import { NOTES_KEY } from '../scratch/api';

const FILTERS: { value: HistoryFilter; label: string }[] = [
  { value: 'all', label: 'Everything' },
  { value: 'changes', label: 'Changes' },
  { value: 'security', label: 'Sign-ins and security' },
];

async function fetchHistory(filter: HistoryFilter, cursor: string | null) {
  const params = new URLSearchParams({ filter, limit: '30' });
  if (cursor) params.set('cursor', cursor);
  const res = await fetch(`/api/history?${params.toString()}`, {
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`history: ${res.status}`);
  return historyPageSchema.parse(await res.json());
}

const ACTOR: Record<string, string> = {
  owner: 'You',
  system: 'System',
  ai: 'AI',
  unknown: 'Someone',
};

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function HistoryPage() {
  const client = useQueryClient();
  const [filter, setFilter] = useState<HistoryFilter>('all');
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const history = useInfiniteQuery({
    queryKey: ['history', filter],
    queryFn: ({ pageParam }) => fetchHistory(filter, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  async function undo(item: HistoryItem) {
    setBusy(item.id);
    setProblems((p) => ({ ...p, [item.id]: '' }));
    try {
      await undoChange(item.id);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['history'] }),
        client.invalidateQueries({ queryKey: NOTES_KEY }),
      ]);
    } catch (err) {
      setProblems((p) => ({ ...p, [item.id]: describeCommandError(err) }));
    } finally {
      setBusy(null);
    }
  }

  const items = history.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <main className="page">
      <p className="back">
        <Link to="/">← Home</Link>
      </p>
      <h1>History</h1>
      <p className="version">Every change, and every sign-in, newest first.</p>

      <div className="filters" role="group" aria-label="Show">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            aria-pressed={filter === f.value}
            className={filter === f.value ? 'primary' : undefined}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <section className="card" aria-label="History" data-testid="history">
        {history.isPending && <p role="status">Loading…</p>}
        {history.isError && (
          <p role="alert" className="error-text">
            Couldn't load the history.{' '}
            <button type="button" onClick={() => void history.refetch()}>
              Try again
            </button>
          </p>
        )}
        {history.data && items.length === 0 && <p className="empty">Nothing here yet.</p>}
        {items.length > 0 && (
          <ul className="rows">
            {items.map((item) => (
              <li key={`${item.kind}-${item.id}`} data-kind={item.kind} data-testid="history-item">
                <div>
                  <p className="history-summary">{item.summary}</p>
                  <p className="hint">
                    <time dateTime={item.at}>{formatWhen(item.at)}</time> ·{' '}
                    {ACTOR[item.actor] ?? item.actor}
                    {item.device ? ` · ${item.device}` : ''}
                    {item.network ? ` · ${item.network}` : ''}
                    {item.undone && <span className="badge">Undone</span>}
                  </p>
                  {problems[item.id] && (
                    <p role="alert" className="error-text">
                      {problems[item.id]}
                    </p>
                  )}
                </div>
                {item.undoable && (
                  <button type="button" disabled={busy !== null} onClick={() => void undo(item)}>
                    Undo
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {history.hasNextPage && (
          <button
            type="button"
            disabled={history.isFetchingNextPage}
            onClick={() => void history.fetchNextPage()}
          >
            {history.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        )}
      </section>
    </main>
  );
}
